/**
 * SharePointFolderPicker — tenant-wide site search + folder browser for the
 * Build-an-Agent wizard (AA-207).
 *
 * Step 1: search UniFirst SharePoint sites via GET /api/me/sharepoint/sites
 * Step 2: browse the default document library via GET /api/me/sharepoint/browse
 *
 * Permission errors (403, SHAREPOINT_MEMBERSHIP_REQUIRED) are surfaced inline.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { File as FileIcon, Folder, X } from 'lucide-react';
import { API_BASE, authFetch } from '../pages/admin/constants';

// Mirrors INDEXABLE_EXTENSIONS in apps/express-api/services/selfServiceIndexService.js.
// Files outside this set still show in the picker so users see the full
// folder contents, but we tag them as "won't be indexed" so the wizard's
// preview matches what the Function App will actually ingest.
const INDEXABLE_EXTENSIONS = new Set([
  '.pdf',
  '.docx',
  '.doc',
  '.pptx',
  '.ppt',
  '.xlsx',
  '.xls',
  '.txt',
  '.md',
  '.csv',
  '.html',
  '.htm',
]);

function getExtension(name: string): string {
  const idx = name.lastIndexOf('.');
  if (idx <= 0 || idx === name.length - 1) return '';
  return name.substring(idx).toLowerCase();
}

function formatBytes(size: number | undefined): string {
  if (!size || size <= 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} MB`;
  return `${(size / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

export interface KnowledgeFolderSelection {
  /** Graph site id (hostname:path or comma-separated id). */
  site: string;
  /**
   * One or more folder paths within the site's default document
   * library. Multi-select is the modern shape; single-folder pickers
   * collapse this to a single-element array. Empty string represents
   * the library root, which is the default when nothing is checked.
   *
   * Order is the order the user picked them, which is the order the
   * indexer traverses on initial ingest.
   */
  folderPaths: string[];
  /**
   * Legacy single-folder field. Always set to folderPaths[0] for
   * back-compat with consumers that haven't migrated yet (mainly
   * the agent record's `sourceFolder` column and any older callers
   * that read it). New code should prefer folderPaths.
   */
  folderPath: string;
  siteWebUrl?: string;
  siteName?: string;
}

interface SiteSearchHit {
  id: string;
  name: string;
  webUrl?: string;
  description?: string;
}

interface BrowseItem {
  name: string;
  itemType?: string;
  isFolder?: boolean;
  webUrl?: string;
  size?: number;
  childCount?: number;
}

interface BrowseResponse {
  success: boolean;
  site?: string;
  path: string;
  count: number;
  items: BrowseItem[];
  error?: string;
  code?: string;
}

interface SitesResponse {
  success: boolean;
  sites: SiteSearchHit[];
  hint?: string;
}

interface Props {
  value?: KnowledgeFolderSelection | null;
  onChange: (selection: KnowledgeFolderSelection | null) => void;
  readOnly?: boolean;
}

const SharePointFolderPicker: React.FC<Props> = ({ value, onChange, readOnly = false }) => {
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sites, setSites] = useState<SiteSearchHit[]>([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState<string | null>(null);
  const [sitesHint, setSitesHint] = useState<string | null>(null);

  const [selectedSite, setSelectedSite] = useState<SiteSearchHit | null>(null);
  const [currentPath, setCurrentPath] = useState('');
  const [items, setItems] = useState<BrowseItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  // Multi-select state. Holds the set of folder paths the user has
  // checked. Order is preserved so the chip list matches selection
  // order (also the order the backend will iterate them on ingest).
  // Seeded from `value` so the picker reopens with the same
  // selection it last submitted, and re-syncs when the parent
  // resets `value` (e.g. user picks a different site).
  const [selectedPaths, setSelectedPaths] = useState<string[]>(() =>
    Array.isArray(value?.folderPaths) ? [...value!.folderPaths] : value?.folderPath ? [value.folderPath] : []
  );

  useEffect(() => {
    // Resync when the parent swaps `value` out from under us — e.g.
    // user clicks "Pick a different site" higher up and the
    // selection clears. Without this, a stale `selectedPaths` set
    // would survive across site changes and confuse the chip list.
    if (!value || !value.site) {
      setSelectedPaths([]);
      return;
    }
    if (Array.isArray(value.folderPaths)) {
      setSelectedPaths([...value.folderPaths]);
    } else if (value.folderPath) {
      setSelectedPaths([value.folderPath]);
    }
  }, [value?.site]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const q = debouncedSearch.trim();
    if (q.length < 2) {
      setSites([]);
      setSitesError(null);
      setSitesHint(null);
      return;
    }

    let cancelled = false;
    setSitesLoading(true);
    setSitesError(null);
    setSitesHint(null);
    authFetch(`${API_BASE}/me/sharepoint/sites?search=${encodeURIComponent(q)}`)
      .then(async (res) => {
        const data = (await res.json()) as SitesResponse & { error?: string };
        if (!res.ok || !data.success) {
          throw new Error(data.error || `${res.status}`);
        }
        if (!cancelled) {
          setSites(data.sites || []);
          setSitesHint(data.hint || null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setSites([]);
          setSitesError(err instanceof Error ? err.message : 'Search failed');
        }
      })
      .finally(() => {
        if (!cancelled) setSitesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch]);

  const fetchFolder = useCallback(async (siteId: string, path: string) => {
    setLoading(true);
    setError(null);
    setErrorCode(null);
    try {
      const params = new URLSearchParams();
      params.set('site', siteId);
      params.set('path', path);
      const res = await authFetch(`${API_BASE}/me/sharepoint/browse?${params.toString()}`);
      const data: BrowseResponse = await res.json();
      if (!res.ok || !data.success) {
        setError(data.error || `${res.status} ${res.statusText}`);
        setErrorCode(data.code || null);
        setItems([]);
        return;
      }
      // Keep both folders and files. Folders are still the only thing
      // a user can navigate into / select for ingestion, but listing
      // the documents alongside makes it obvious *why* the folder is
      // (or isn't) the right pick — saves a separate "what's in here"
      // round-trip back to SharePoint after attaching the agent.
      setItems(data.items || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load folder');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Folders first, then files; alphabetical within each group. Avoids
  // shuffling the list every render.
  const { folders, files, indexableFileCount, totalFileCount } = useMemo(() => {
    const fld: BrowseItem[] = [];
    const fls: BrowseItem[] = [];
    for (const it of items) {
      if (it.isFolder === true || it.itemType === 'folder') fld.push(it);
      else fls.push(it);
    }
    const byName = (a: BrowseItem, b: BrowseItem) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    fld.sort(byName);
    fls.sort(byName);
    const indexable = fls.filter((f) => INDEXABLE_EXTENSIONS.has(getExtension(f.name))).length;
    return {
      folders: fld,
      files: fls,
      indexableFileCount: indexable,
      totalFileCount: fls.length,
    };
  }, [items]);

  useEffect(() => {
    if (selectedSite?.id) {
      fetchFolder(selectedSite.id, currentPath);
    }
  }, [selectedSite?.id, currentPath, fetchFolder]);

  const handleSelectSite = (site: SiteSearchHit) => {
    setSelectedSite(site);
    setCurrentPath('');
    setError(null);
    setErrorCode(null);
    setItems([]);
  };

  const handlePickDifferentSite = () => {
    setSelectedSite(null);
    setCurrentPath('');
    setItems([]);
    setError(null);
    setErrorCode(null);
    setSelectedPaths([]);
    onChange(null);
  };

  /**
   * Emit the current multi-select to the parent. Called on every
   * checkbox toggle and chip-remove so the wizard's state always
   * mirrors the picker — no separate "Confirm" step. An empty
   * selection emits null so the wizard's "knowledgeSource ready"
   * gate works the same way it did before multi-select shipped.
   */
  const emitSelection = useCallback(
    (paths: string[]) => {
      if (!selectedSite) return;
      if (paths.length === 0) {
        onChange(null);
        return;
      }
      onChange({
        site: selectedSite.id,
        folderPaths: [...paths],
        folderPath: paths[0],
        siteWebUrl: selectedSite.webUrl,
        siteName: selectedSite.name,
      });
    },
    [onChange, selectedSite]
  );

  const togglePath = (path: string) => {
    setSelectedPaths((prev) => {
      const next = prev.includes(path) ? prev.filter((p) => p !== path) : [...prev, path];
      emitSelection(next);
      return next;
    });
  };

  const removePath = (path: string) => {
    setSelectedPaths((prev) => {
      const next = prev.filter((p) => p !== path);
      emitSelection(next);
      return next;
    });
  };

  /**
   * Build the absolute path to a child folder of the current view.
   * Mirrors how the backend's listSiteItems composes drive-relative
   * paths so the resulting string can be sent to the indexer
   * unchanged.
   */
  const childPath = (childName: string) =>
    currentPath ? `${currentPath}/${childName}` : childName;

  /**
   * Tagline for the current folder when it appears in the
   * "selected" chip list. Library-root selections show as a special
   * label so users don't see an empty chip.
   */
  const labelForPath = (path: string) => path || '(library root)';

  const breadcrumbs = currentPath
    .split('/')
    .filter(Boolean)
    .reduce<{ name: string; path: string }[]>((acc, part) => {
      const prevPath = acc.length === 0 ? '' : acc[acc.length - 1].path;
      const path = prevPath ? `${prevPath}/${part}` : part;
      acc.push({ name: part, path });
      return acc;
    }, []);

  // --- Step 1: site search ---
  if (!selectedSite) {
    return (
      <div
        style={{
          border: '1px solid var(--idx-border-strong, #cfd2d6)',
          background: 'var(--idx-bg-secondary, #f8f9fa)',
          borderRadius: 8,
          padding: 12,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
            marginBottom: 10,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 14 }}>Find a SharePoint site</div>
          <span
            title="Search any UniFirst SharePoint site you are a member of (team sites, department sites, AskAldoKB, and more). You must have membership on a site to browse folders there."
            style={{
              fontSize: 12,
              color: 'var(--idx-text-tertiary, #6f747b)',
              cursor: 'help',
              borderBottom: '1px dotted #9da2a8',
            }}
          >
            How to use this
          </span>
        </div>

        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Type at least 2 characters (site name, URL path, etc.)"
          disabled={readOnly}
          style={{
            width: '100%',
            padding: '8px 10px',
            borderRadius: 6,
            border: '1px solid var(--idx-border, #cfd2d6)',
            fontSize: 14,
            marginBottom: 10,
          }}
        />

        {debouncedSearch.trim().length > 0 && debouncedSearch.trim().length < 2 && (
          <div style={{ fontSize: 12, color: '#6f747b', marginBottom: 8 }}>
            Enter at least 2 characters to search across the tenant.
          </div>
        )}

        {sitesLoading && (
          <div style={{ padding: 12, color: 'var(--idx-text-secondary, #6f747b)' }}>Searching…</div>
        )}
        {sitesError && (
          <div
            style={{
              padding: 12,
              color: '#991b1b',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            {sitesError}
          </div>
        )}

        {/*
          When the per-user membership filter drops sites we received from
          search, the API returns a `hint` describing what happened. Show
          that text instead of the generic "No sites matched" so users
          know the difference between "nothing exists matching foo" and
          "results exist but you can't see them — talk to a SharePoint
          admin." For partial filtering (some kept, some hidden) the hint
          renders above the list as a quiet annotation.
        */}
        {!sitesLoading && !sitesError && debouncedSearch.trim().length >= 2 && sites.length === 0 && (
          <div
            style={{
              padding: 10,
              fontSize: 13,
              color: sitesHint ? '#92400e' : '#6f747b',
              background: sitesHint ? '#fffbeb' : 'transparent',
              border: sitesHint ? '1px solid #fde68a' : 'none',
              borderRadius: sitesHint ? 6 : 0,
            }}
          >
            {sitesHint || 'No sites matched. Try another keyword.'}
          </div>
        )}

        {!sitesLoading && sites.length > 0 && sitesHint && (
          <div style={{ padding: '6px 8px', fontSize: 12, color: '#6f747b', fontStyle: 'italic' }}>
            {sitesHint}
          </div>
        )}

        {!sitesLoading && sites.length > 0 && (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 260, overflowY: 'auto' }}>
            {sites.map((s) => (
              <li key={s.id} style={{ borderBottom: '1px solid var(--idx-border, #e3e5e8)' }}>
                <button
                  type="button"
                  disabled={readOnly}
                  onClick={() => handleSelectSite(s)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 8px',
                    border: 'none',
                    background: 'transparent',
                    cursor: readOnly ? 'not-allowed' : 'pointer',
                  }}
                >
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{s.name}</div>
                  {s.webUrl && (
                    <div style={{ fontSize: 12, color: '#047857', wordBreak: 'break-all' }}>{s.webUrl}</div>
                  )}
                  {s.description ? (
                    <div style={{ fontSize: 12, color: '#6f747b', marginTop: 4 }}>{s.description}</div>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        )}

        {value?.site && (
          <div style={{ marginTop: 10, fontSize: 12, color: '#047857' }}>
            Current selection: {value.siteName || value.siteWebUrl || value.site}
            {value.folderPaths && value.folderPaths.length > 0
              ? ` / ${value.folderPaths.length} folder${value.folderPaths.length === 1 ? '' : 's'}`
              : value.folderPath
                ? ` / ${value.folderPath}`
                : ''}
          </div>
        )}
      </div>
    );
  }

  // --- Step 2: folder browser ---
  const rootLabel = selectedSite.name;

  return (
    <div
      style={{
        border: '1px solid var(--idx-border-strong, #cfd2d6)',
        background: 'var(--idx-bg-secondary, #f8f9fa)',
        borderRadius: 8,
        padding: 12,
      }}
    >
      <div style={{ marginBottom: 10 }}>
        <button
          type="button"
          onClick={handlePickDifferentSite}
          disabled={readOnly}
          style={{
            background: 'transparent',
            border: 'none',
            color: 'var(--idx-accent, #047857)',
            cursor: readOnly ? 'not-allowed' : 'pointer',
            padding: 0,
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          ← Pick a different site
        </button>
      </div>

      {selectedSite.webUrl && (
        <div style={{ fontSize: 12, color: '#6f747b', marginBottom: 8, wordBreak: 'break-all' }}>
          {selectedSite.webUrl}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
        <button
          type="button"
          onClick={() => setCurrentPath('')}
          style={{
            background: 'transparent',
            border: 'none',
            color: currentPath === '' ? 'var(--idx-text-primary, #1f2124)' : 'var(--idx-accent, #047857)',
            cursor: 'pointer',
            padding: 0,
            fontWeight: currentPath === '' ? 600 : 500,
          }}
        >
          {rootLabel}
        </button>
        {breadcrumbs.map((bc, idx) => (
          <React.Fragment key={bc.path}>
            <span style={{ color: 'var(--idx-text-tertiary, #9da2a8)' }}>/</span>
            <button
              type="button"
              onClick={() => setCurrentPath(bc.path)}
              style={{
                background: 'transparent',
                border: 'none',
                color:
                  idx === breadcrumbs.length - 1
                    ? 'var(--idx-text-primary, #1f2124)'
                    : 'var(--idx-accent, #047857)',
                cursor: 'pointer',
                padding: 0,
                fontWeight: idx === breadcrumbs.length - 1 ? 600 : 500,
              }}
            >
              {bc.name}
            </button>
          </React.Fragment>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 12, color: 'var(--idx-text-secondary, #6f747b)' }}>Loading folders…</div>
      ) : error ? (
        <div
          style={{
            padding: 12,
            color: '#991b1b',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            borderRadius: 6,
          }}
        >
          <div style={{ fontWeight: 600, marginBottom: 4 }}>Could not browse SharePoint</div>
          <div style={{ fontSize: 13 }}>{error}</div>
          {errorCode === 'SHAREPOINT_MEMBERSHIP_REQUIRED' && (
            <div style={{ marginTop: 8, fontSize: 12 }}>
              You must be a member or owner of this site. Ask a site owner to add you, then try again.
              Large group memberships may take time to appear; nested groups can be missed by automated checks.
            </div>
          )}
        </div>
      ) : items.length === 0 ? (
        <div style={{ padding: 12, color: 'var(--idx-text-secondary, #6f747b)' }}>
          This folder is empty. Click <strong>Use this folder</strong> to attach the agent here anyway,
          or go back up.
        </div>
      ) : (
        <>
          {totalFileCount > 0 && (
            <div
              style={{
                fontSize: 12,
                color: '#6f747b',
                marginBottom: 6,
                padding: '0 4px',
              }}
            >
              {folders.length} folder{folders.length === 1 ? '' : 's'},{' '}
              {totalFileCount} file{totalFileCount === 1 ? '' : 's'}
              {totalFileCount > indexableFileCount && (
                <span style={{ color: '#92400e' }}>
                  {' '}
                  ({indexableFileCount} will be indexed; the rest are unsupported file types)
                </span>
              )}
            </div>
          )}
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              maxHeight: 320,
              overflowY: 'auto',
              border: '1px solid var(--idx-border, #e3e5e8)',
              borderRadius: 6,
              background: 'var(--idx-bg-primary, #ffffff)',
            }}
          >
            {folders.map((item) => {
              const path = childPath(item.name);
              const checked = selectedPaths.includes(path);
              return (
                <li
                  key={`folder:${item.name}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '4px 6px 4px 10px',
                    background: checked ? 'var(--brand-50, #ecfdf5)' : 'transparent',
                    borderTop: '1px solid var(--idx-border, #f1f2f4)',
                  }}
                >
                  {/*
                    Checkbox is the selection affordance. The folder
                    name is a separate button that browses INTO the
                    folder so users can drill down without affecting
                    selection. Two distinct hit targets keep the two
                    actions from accidentally overlapping.
                  */}
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={readOnly}
                    onChange={() => togglePath(path)}
                    aria-label={`Select folder ${item.name}`}
                    style={{ flexShrink: 0, cursor: readOnly ? 'not-allowed' : 'pointer' }}
                  />
                  <button
                    type="button"
                    onClick={() => setCurrentPath(path)}
                    title="Browse into this folder"
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flex: 1,
                      textAlign: 'left',
                      padding: '4px 4px',
                      border: 'none',
                      borderRadius: 4,
                      background: 'transparent',
                      cursor: 'pointer',
                      color: 'var(--idx-text-primary, #1f2124)',
                    }}
                    onMouseEnter={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background =
                        checked ? 'var(--brand-100, #d1fae5)' : 'var(--idx-bg-hover, #f1f2f4)';
                    }}
                    onMouseLeave={(e) => {
                      (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                    }}
                  >
                    <Folder size={16} aria-hidden style={{ color: '#6f747b', flexShrink: 0 }} />
                    <span>{item.name}</span>
                    {typeof item.childCount === 'number' && (
                      <span style={{ marginLeft: 'auto', fontSize: 12, color: '#6f747b' }}>
                        {item.childCount} item{item.childCount === 1 ? '' : 's'}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
            {/*
              Files are read-only rows (no click handler). The indexer
              ingests entire folders, not individual files, so making
              files selectable would suggest a workflow we don't
              actually support. Showing them inline still answers the
              "what's in here?" question that drives most folder picks.
            */}
            {files.map((item) => {
              const ext = getExtension(item.name);
              const indexable = ext && INDEXABLE_EXTENSIONS.has(ext);
              const sizeLabel = formatBytes(item.size);
              return (
                <li
                  key={`file:${item.name}`}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px 10px',
                    color: indexable
                      ? 'var(--idx-text-secondary, #53575d)'
                      : 'var(--idx-text-tertiary, #9da2a8)',
                    fontSize: 13,
                    borderTop: '1px solid var(--idx-border, #f1f2f4)',
                  }}
                  title={
                    indexable
                      ? `Will be indexed (${ext})`
                      : `Skipped: ${ext || 'unknown extension'} not supported by the indexer`
                  }
                >
                  <FileIcon
                    size={14}
                    aria-hidden
                    style={{
                      color: indexable ? '#047857' : '#9da2a8',
                      flexShrink: 0,
                    }}
                  />
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.name}
                  </span>
                  {sizeLabel && (
                    <span style={{ fontSize: 11, color: '#9da2a8', flexShrink: 0 }}>{sizeLabel}</span>
                  )}
                  {!indexable && ext && (
                    <span
                      style={{
                        fontSize: 10,
                        color: '#92400e',
                        background: '#fffbeb',
                        border: '1px solid #fde68a',
                        borderRadius: 3,
                        padding: '1px 5px',
                        flexShrink: 0,
                      }}
                    >
                      not indexed
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}

      <div
        style={{
          marginTop: 10,
          paddingTop: 10,
          borderTop: '1px solid var(--idx-border, #e3e5e8)',
        }}
      >
        {/*
          The "include current view" affordance covers a UX gap:
          checkboxes only appear next to subfolders, so without
          this button there's no way to select the folder you're
          currently *inside*. Lets users say "index this whole
          folder" by browsing into it and adding it explicitly.

          IMPORTANT: only render the button when the user is actually
          INSIDE a folder (currentPath !== ''). At the library root,
          `currentPath` is '' which means "the entire library", and
          users expecting "Add this folder" to commit their checkbox
          selections were instead silently adding the whole library
          alongside their choices. Hiding the button at root also
          forces the user to make a real choice (check children or
          drill in) instead of accidentally indexing everything.
        */}
        {!readOnly && currentPath !== '' && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 10,
              marginBottom: 10,
            }}
          >
            <div style={{ fontSize: 12, color: '#6f747b' }}>
              Currently viewing:{' '}
              <code style={{ background: '#f1f2f4', padding: '2px 6px', borderRadius: 4 }}>
                {labelForPath(currentPath)}
              </code>
            </div>
            <button
              type="button"
              onClick={() => {
                if (!selectedPaths.includes(currentPath)) {
                  togglePath(currentPath);
                }
              }}
              disabled={loading || selectedPaths.includes(currentPath)}
              title={
                selectedPaths.includes(currentPath)
                  ? 'This folder is already in your selection'
                  : 'Add the folder you are currently viewing to the selection'
              }
              style={{
                background: selectedPaths.includes(currentPath)
                  ? 'var(--idx-bg-secondary, #f1f2f4)'
                  : 'var(--brand-50, #ecfdf5)',
                color: selectedPaths.includes(currentPath)
                  ? '#9da2a8'
                  : 'var(--brand-700, #047857)',
                border: '1px solid var(--brand-600, #047857)',
                padding: '6px 12px',
                borderRadius: 6,
                cursor:
                  loading || selectedPaths.includes(currentPath) ? 'not-allowed' : 'pointer',
                fontWeight: 500,
                fontSize: 12,
              }}
            >
              {selectedPaths.includes(currentPath) ? 'Added' : 'Add this folder'}
            </button>
          </div>
        )}

        <div style={{ fontSize: 12, color: '#6f747b', marginBottom: 6 }}>
          {selectedPaths.length === 0 ? (
            <span>
              No folders selected yet. Check folders above to include them
              {currentPath !== '' && (
                <>
                  , or use <strong>Add this folder</strong> to add the one you&apos;re viewing
                </>
              )}
              .
            </span>
          ) : (
            <span>
              <strong>{selectedPaths.length}</strong> folder
              {selectedPaths.length === 1 ? '' : 's'} selected. Add more, or remove from the
              list below.
            </span>
          )}
        </div>

        {selectedPaths.length > 0 && (
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
            }}
          >
            {selectedPaths.map((p) => (
              <span
                key={`chip:${p}`}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '3px 4px 3px 8px',
                  fontSize: 12,
                  background: 'var(--brand-50, #ecfdf5)',
                  border: '1px solid var(--brand-600, #047857)',
                  color: 'var(--brand-700, #047857)',
                  borderRadius: 12,
                  maxWidth: '100%',
                }}
                title={p || '(library root)'}
              >
                <Folder size={12} aria-hidden />
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: 220,
                  }}
                >
                  {labelForPath(p)}
                </span>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => removePath(p)}
                    aria-label={`Remove ${labelForPath(p)} from selection`}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      cursor: 'pointer',
                      color: 'inherit',
                      padding: 0,
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    <X size={12} aria-hidden />
                  </button>
                )}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SharePointFolderPicker;
